import { computed } from 'vue';
import { interpret } from 'xstate';
import { useActor } from '@xstate/vue';

import { CurrentKey, LoanType, RelationshipType, APPLICATION_CONTEXT } from '@/helpers/const';
import { formatAddress } from '@/helpers/format';
import { includesEnum } from '@/helpers/includes-enum';
import { hasAssets } from '@/helpers/typeGuards';
import {
  initMachine,
  loanApplicationMachineBroker,
  type BrokerLoanAppMachineContext,
} from '@/helpers/loanBrokerAppMachine';
import { BrokerApplicationProcessSteps, BrokerProcessStep, BrokerProcessStepNames, StepStatus } from '@/types';

import type {
  Address,
  Applicant,
  ApplicantId,
  AssetId,
  BrokerApplicationProcessStepStatus,
  BrokerProcessStepEnum,
  LoanApplication,
  PropertyId,
} from '@/types';

const appKey = localStorage.getItem(CurrentKey) ?? '';
const savedState = JSON.parse(localStorage.getItem(appKey) as string);
const initialState: BrokerLoanAppMachineContext = savedState ? savedState : initMachine();

const service = interpret(loanApplicationMachineBroker.withContext(initialState)).start();

service.onTransition((state) => {
  if (state.value !== 'gotoReady' && state.value !== 'start') {
    const currentStep = state.value as BrokerProcessStepEnum;
    if (state.context.status[currentStep] === StepStatus.Incomplete) {
      state.context.status[currentStep] = StepStatus.Ready;
    }

    state.context.current = currentStep;
    const loanApp = state.context.loanApp;
    if (loanApp.applicationId && loanApp.userType) {
      const key = `${loanApp.actionType}-${loanApp.applicationId}`;
      const appState = JSON.stringify(state.context);
      localStorage.setItem(CurrentKey, key);
      localStorage.setItem(key, appState);
    }
  }
});

const processSteps = Object.entries(BrokerApplicationProcessSteps).map(([step, info]) => ({
  id: step as BrokerProcessStepEnum,
  ...info,
}));

/**
 * Steps that require end debt > 0 to be shown
 * Note: Exit Strategy also requires Single Security loan type
 * Exit Strategy is special-cased: also Purchase Only loans
 */
const DEBT_CONDITIONAL_STEPS: BrokerProcessStepEnum[] = [
  BrokerProcessStep.Income,
  BrokerProcessStep.Expenses,
  BrokerProcessStep.Assets,
  BrokerProcessStep.ExitStrategy,
];

/**
 * Checks if a step is one of the debt-conditional financial steps
 */
const isDebtConditionalStep = (stepId: BrokerProcessStepEnum): boolean => {
  return includesEnum(stepId, DEBT_CONDITIONAL_STEPS);
};

/**
 * Determines if financial steps should be shown based on loan conditions
 * @param loanApp - The loan application data
 * @param isSecondaryMarried - Whether secondary applicant is married
 * @param fromContext - The context from which application was initiated
 * @returns True if financial steps should be displayed
 */
const shouldShowFinancialSteps = (
  loanApp: LoanApplication,
  isSecondaryMarried: boolean,
  fromContext?: string,
): boolean => {
  // Always show for CRM
  if (fromContext === APPLICATION_CONTEXT.CRM) return true;

  // Show if has end debt and not secondary married
  if (loanApp.endDebt > 0 && !isSecondaryMarried) return true;

  return false;
};

/**
 * Checks if step should be visible in secondary declaration flow
 */
const isSecondaryDeclarationFlow = (loanApp: LoanApplication, authorisedUser: Record<string, string>): boolean => {
  return loanApp.userType === 'Secondary' && !authorisedUser.id && loanApp.actionType === 'updateExisting';
};

/**
 * Special case: Single Security loans always show Assets step
 */
const isSingleSecurityAssets = (stepId: BrokerProcessStepEnum, loanType?: string): boolean => {
  return loanType === LoanType.SINGLE_SECURITY && stepId === BrokerProcessStep.Assets;
};

/**
 * Determines if a step should be visible in the sidebar
 */
const shouldShowStep = (
  step: { id: BrokerProcessStepEnum; actions: string[] },
  loanApp: LoanApplication,
  isSecondaryMarried: boolean,
  authorisedUser: Record<string, string>,
  fromContext?: string,
): boolean => {
  // Always hide Finished step from sidebar
  if (step.id === BrokerProcessStep.Finished) return false;

  // Must have user type and matching action
  if (!loanApp.userType || !step.actions.includes(loanApp.actionType)) {
    return false;
  }

  if (step.id === BrokerProcessStep.ExitStrategy) {
    if (loanApp.loanType === LoanType.PURCHASE_ONLY) {
      return true;
    }
    return (
      loanApp.loanType === LoanType.SINGLE_SECURITY &&
      shouldShowFinancialSteps(loanApp, isSecondaryMarried, fromContext)
    );
  }

  // If not a debt-conditional step, always show
  if (!isDebtConditionalStep(step.id)) return true;

  // For debt-conditional steps, check conditions
  if (shouldShowFinancialSteps(loanApp, isSecondaryMarried, fromContext)) {
    return true;
  }

  // Secondary declaration flow shows financial steps
  if (isSecondaryDeclarationFlow(loanApp, authorisedUser)) {
    return true;
  }

  // Single Security always shows Assets
  if (isSingleSecurityAssets(step.id, loanApp.loanType)) {
    return true;
  }

  // For Assets step, only show if the loan application contains any assets
  if (step.id === BrokerProcessStep.Assets) {
    if (hasAssets(loanApp)) return true;
  }

  // Otherwise, hide debt-conditional steps
  return false;
};

/**
 * Gets the current valid step and its order from the process steps
 * @param currentStepId - The current step ID from state
 * @returns Object containing validated step and its order
 */
const getCurrentStepInfo = (currentStepId: BrokerProcessStepEnum) => {
  const validStep = BrokerProcessStepNames.includes(currentStepId) ? currentStepId : BrokerProcessStepNames[0];
  return {
    step: validStep,
    order: BrokerApplicationProcessSteps[validStep].order,
  };
};

/**
 * Calculates the display status for a step based on its current status and position
 */
const calculateStepStatus = (
  step: { id: BrokerProcessStepEnum },
  stepStatus: StepStatus,
  currentStepOrder: number,
): StepStatus => {
  if (stepStatus === StepStatus.Incomplete && BrokerApplicationProcessSteps[step.id].order < currentStepOrder) {
    return StepStatus.Skipped;
  }
  return stepStatus;
};

export function useBrokerLoanAppProcess() {
  const { state, send } = useActor(service);
  const currentStep = computed(() => state.value.context.current);
  const loanApp = computed(() => state.value.context.loanApp);
  const authorisedUser = state.value.context.authorisedUser;

  const secondaryApplicant = computed(() => {
    return loanApp.value.actionType === 'updateExisting'
      ? loanApp.value.applicants.find((a) => a.id === state.value.context.authorisedUser.id && a.type === 'secondary')
      : undefined;
  });

  const isSecondaryMarried = computed(
    () => secondaryApplicant.value?.relationshipToPrimary === RelationshipType.MARRIED_OR_DEFACTO,
  );

  const stepper = computed<BrokerApplicationProcessStepStatus[]>(() => {
    const { order: currentStepOrder } = getCurrentStepInfo(state.value.context.current);
    return processSteps.reduce<BrokerApplicationProcessStepStatus[]>((visibleSteps, step) => {
      if (!shouldShowStep(step, loanApp.value, isSecondaryMarried.value, authorisedUser, state.value.context.from)) {
        return visibleSteps;
      }

      const stepStatus = state.value.context.status[step.id];
      const status = calculateStepStatus(step, stepStatus, currentStepOrder);

      if (status === StepStatus.Skipped) {
        return visibleSteps;
      }

      visibleSteps.push({ ...step, status });
      return visibleSteps;
    }, []);
  });

  const loanProperties = computed(() =>
    [
      ...loanApp.value.incomingProperties,
      ...loanApp.value.outgoingProperties,
      ...(loanApp.value?.supportingProperties ?? []),
    ].reduce((obj, item) => {
      obj[item.id] = formatAddress(item.address as Address);
      return obj;
    }, {} as Record<PropertyId | AssetId, string>),
  );
  const loanApplicants = computed<Record<string, Applicant>>(() =>
    loanApp.value.applicants.reduce((obj, item) => {
      obj[item.id as string] = item;
      return obj;
    }, {} as Record<ApplicantId, Applicant>),
  );

  const saveAndGoNext = (updatedData: Partial<LoanApplication> = {}) => {
    if (currentStep.value === BrokerProcessStep.SubmitApplication) {
      localStorage.setItem(CurrentKey, '');
    }
    send({ type: 'NEXT', loanApp: { ...state.value.context.loanApp, ...updatedData } });
  };

  const goBack = () => {
    send('BACK');
  };

  const restart = (isBroker?: boolean) => {
    send(isBroker === undefined ? 'RESTART' : 'RESTART_BROKER');
  };

  return {
    state,
    service,
    stepper,
    currentStep, // remove?
    authorisedUser,
    loanApp,
    // targetApplicant: state.value.context.targetApplicant,
    loanProperties,
    loanApplicants,
    send,
    saveAndGoNext,
    goBack,
    restart,
  };
}
